'use client'

import { useEffect, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import { VideoOverlay } from '@homie/video-player'
import type { OnboardingVideo } from '@homie/video-player'
import { CirclePlay, ChevronRight } from 'lucide-react'

function VideoRow({
  video,
  onPlay,
  isLast,
}: {
  video: OnboardingVideo
  onPlay: () => void
  isLast: boolean
}) {
  return (
    <>
      <button
        onClick={onPlay}
        className="w-full flex items-center gap-4 py-4 cursor-pointer text-left"
      >
        {/* Play icon with duration below */}
        <div className="flex flex-col items-center shrink-0">
          <CirclePlay size={32} strokeWidth={1} />
          <span className="text-[14px] text-gray-500 mt-1">{video.duration}</span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-l font-bold">{video.title}</div>
          <div className="text-l text-gray-600">{video.description}</div>
        </div>

        {/* Chevron */}
        <ChevronRight size={24} className="shrink-0 text-gray-400" />
      </button>
      {!isLast ? (
        <hr className="border-t border-dashed border-black" />
      ) : (
        <div className="pb-4" />
      )}
    </>
  )
}

function VideoSection({
  title,
  videos,
  onPlayVideo,
}: {
  title: string
  videos: OnboardingVideo[]
  onPlayVideo: (video: OnboardingVideo) => void
}) {
  return (
    <div className="flex flex-col">
      {/* Section header */}
      <h2 className="text-l font-bold tracking-wider uppercase mb-2">{title}</h2>
      <hr className="border-t border-dashed border-black" />

      {/* Video list */}
      {videos.map((video, index) => (
        <VideoRow
          key={video.id}
          video={video}
          onPlay={() => onPlayVideo(video)}
          isLast={index === videos.length - 1}
        />
      ))}
    </div>
  )
}

type LearnPageClientProps = {
  gettingStarted: OnboardingVideo[]
  sectionGuides: OnboardingVideo[]
  tipsTricks: OnboardingVideo[]
}

export function LearnPageClient({
  gettingStarted,
  sectionGuides,
  tipsTricks,
}: LearnPageClientProps) {
  const t = useTranslations('learnPage')
  const [selectedVideo, setSelectedVideo] = useState<OnboardingVideo | null>(null)
  // Tracks whether the current overlay open came from a hash (no
  // user gesture, autoplay would be blocked) vs. a row click (gesture
  // present). Drives the VideoOverlay's tap-to-play scrim.
  const [requireTapToPlay, setRequireTapToPlay] = useState(false)

  // URL is the source of truth. Two flows:
  //
  // - Inbound: page loads with `#video-<id>` (from email link or
  //   shared URL) → useEffect reads hash and opens that video.
  // - Outbound: clicking a row pushes `#video-<id>` onto history so
  //   the URL becomes shareable and the browser's back button closes
  //   the overlay. Clicking the X also clears the hash via pushState
  //   (rather than back()) so users who arrived via a hashed link
  //   don't get bounced off /learn entirely.
  //
  // Hash format mirrors the SEO VideoObject @id used in page.tsx.
  const allVideos = useMemo(
    () => [...gettingStarted, ...sectionGuides, ...tipsTricks],
    [gettingStarted, sectionGuides, tipsTricks],
  )
  useEffect(() => {
    function syncFromHash() {
      const hash = window.location.hash.replace(/^#/, '')
      if (!hash.startsWith('video-')) {
        setSelectedVideo(null)
        setRequireTapToPlay(false)
        return
      }
      const id = hash.slice('video-'.length)
      const video = allVideos.find(v => v.id === id)
      setSelectedVideo(video ?? null)
      // Hash-driven open: no user gesture available, so the overlay
      // should show its tap-to-play scrim instead of trying to
      // autoplay (which browsers would block).
      setRequireTapToPlay(!!video)
    }
    syncFromHash()
    // hashchange fires when something else changes the hash
    // (URL bar edit, anchor link). popstate fires for back/forward
    // after our own pushState calls. Listening to both keeps the
    // overlay state in lockstep with the URL.
    window.addEventListener('hashchange', syncFromHash)
    window.addEventListener('popstate', syncFromHash)
    return () => {
      window.removeEventListener('hashchange', syncFromHash)
      window.removeEventListener('popstate', syncFromHash)
    }
  }, [allVideos])

  function openVideo(video: OnboardingVideo) {
    window.history.pushState(null, '', `#video-${video.id}`)
    setSelectedVideo(video)
    // Click-driven open carries a user gesture, so the overlay can
    // autoplay normally — no scrim needed.
    setRequireTapToPlay(false)
  }

  function closeVideo() {
    if (window.location.hash) {
      // Strip the hash without leaving /learn. Using pushState (not
      // history.back) so a user who arrived via a hashed link from
      // email stays on the page when they close the overlay.
      window.history.pushState(
        null,
        '',
        window.location.pathname + window.location.search,
      )
    }
    setSelectedVideo(null)
    setRequireTapToPlay(false)
  }

  return (
    <>
      {/* Page content */}
      <div className="px-6 py-8 flex flex-col gap-10">
        {/* Getting Started section */}
        <VideoSection
          title={t('gettingStarted')}
          videos={gettingStarted}
          onPlayVideo={openVideo}
        />

        {/* Section Guides */}
        <VideoSection
          title={t('sectionGuides')}
          videos={sectionGuides}
          onPlayVideo={openVideo}
        />

        {/* Tips & Tricks */}
        <VideoSection
          title={t('tipsTricks')}
          videos={tipsTricks}
          onPlayVideo={openVideo}
        />
      </div>

      {/* Video overlay */}
      {selectedVideo && (
        <VideoOverlay
          isOpen={!!selectedVideo}
          onClose={closeVideo}
          playbackId={selectedVideo.playbackId}
          title={selectedVideo.title}
          aspectRatio="portrait"
          requireTapToPlay={requireTapToPlay}
        />
      )}
    </>
  )
}
